/**
 * Main Navigation Component
 *
 * This component provides the primary navigation interface for the application.
 * It dynamically renders navigation items based on the application routes and
 * adapts to different screen sizes with responsive design.
 *
 * The Navbar is a critical UI component that integrates with:
 * - Routing system for navigation between pages
 * - Authentication system for login/logout functionality
 * - Responsive design system for mobile/desktop adaptation
 *
 * Key features:
 * - Responsive design with mobile and desktop layouts
 * - Scroll-aware styling (changes appearance on scroll)
 * - Dynamic route rendering from centralized route registry
 * - Authentication-aware UI elements
 */

import {
  faBars,
  faCircleDollarToSlot,
  faX,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import React, { useEffect, useState } from 'react'
import { BoxArrowInRight } from 'react-bootstrap-icons'
import { Link, NavLink } from 'react-router-dom'
import logo from '../../assets/images/academy/logo.png'
import { routes } from '../../routes'
import './styles.scss'

/**
 * Navbar component definition
 *
 * Renders the main navigation bar at the top of the application.
 * This component handles:
 * - Responsive layout switching between mobile and desktop
 * - Navigation menu toggle for mobile view
 * - Scroll-aware styling changes
 * - Dynamic route rendering based on the routes registry
 *
 * @returns {JSX.Element} The rendered Navbar component
 */
const Navbar = () => {
  // State to handle if the header has been scrolled past a certain point
  const [headerScrolled, setHeaderScrolled] = useState(false)
  // State to check if the viewport is mobile size
  const [isMobile, setIsMobile] = useState(window.innerWidth < 960)
  // State to track if the mobile navigation menu is open
  const [navIsOpen, setNavIsOpen] = useState(false)

  // Effect for handling scroll and resize events
  useEffect(() => {
    const onScroll = (e) => setHeaderScrolled(window.scrollY > 140)
    const onResize = (e) => setIsMobile(window.innerWidth < 960)

    document.addEventListener('scroll', onScroll)
    window.addEventListener('resize', onResize)
    return () => {
      document.removeEventListener('scroll', onScroll)
      window.removeEventListener('resize', onResize)
    }
  }, [])

  // Function to toggle mobile navigation menu open/closed
  const toggleNav = () => {
    setNavIsOpen(!navIsOpen)
  }

  // Function to close the mobile navigation menu
  // Used when a navigation link is clicked
  const closeNav = () => {
    setNavIsOpen(false)
  }

  return (
    <nav
      className={`navbar__home ${
        !isMobile && headerScrolled ? 'nav-scrolled' : ''
      }`}
    >
      <Link to="/">
        <div className="logo">
          <img src={logo} alt="KET Academy Logo" onClick={closeNav} />
        </div>
      </Link>

      <div
        className={`nav-items ${isMobile ? 'nav-mobile' : ''} ${
          navIsOpen ? 'show' : 'hide'
        }`}
      >
        <div className="donate">
          <Link to="/donate" onClick={closeNav}>
            <button>
              <FontAwesomeIcon icon={faCircleDollarToSlot} /> Donate
            </button>
          </Link>
        </div>
        <ul className="routes">
          {routes.map((route, index) => (
            <li key={index}>
              <NavLink
                to={route.path}
                className={({ isActive }) =>
                  `nav-link ${isActive ? 'active' : ''}`
                }
                onClick={closeNav}
              >
                {route.name}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="cta">
          <Link to="/account/login" onClick={closeNav}>
            <button title="Sign out">
              <BoxArrowInRight className="text-xl font-black outline-2 inline mr-2" />
              Login
            </button>
          </Link>
        </div>
      </div>
      <div className={`mobile-toggle ${isMobile ? 'show' : 'hide'}`}>
        <button onClick={toggleNav}>
          <FontAwesomeIcon icon={navIsOpen ? faX : faBars} />
        </button>
      </div>
    </nav>
  )
}

export default Navbar
